import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Checkbox,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Paragraph,
  Select,
  Switch,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { useUsesSafetyReps } from '../../../../hooks/queries/hms/useHmsSettingsQuery';
import { safetyRepText } from '../../safetyRepText';
import type { RiskMatrixFactor, RiskMatrixOptions, RiskMatrixSettings } from '../../../../api/hms';
import {
  FIELD_IDS,
  ONE_REPORT_KEYS,
  SEXUAL_HARASSMENT,
  linkError,
} from '../riskMatrixView';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskMatrix.module.css';

type Props = {
  settings: RiskMatrixSettings;
  factors: RiskMatrixFactor[];
  options: RiskMatrixOptions;
  onChange: (next: Partial<RiskMatrixSettings>) => void;
  showUrlErrors: boolean;
};


/** The regulations require these, so they cannot be turned off */
const preventToggle = (event: { preventDefault: () => void }) => event.preventDefault();

export const RulesCard = ({ settings, factors, options, onChange, showUrlErrors }: Props): ReactElement => {
  const { t } = useTranslation();
  const text = safetyRepText(useUsesSafetyReps());
  const whistleblowingError = linkError(settings.whistleblowingUrl);
  const followUpError = linkError(settings.followUpRoutineUrl);
  const factorName = (key: string) =>
    factors.find((factor) => factor.key === key)?.name ?? t(`hrRiskMatrix.rules.factorNames.${key}`);

  const toggleOneReport = (key: string, on: boolean) =>
    onChange({
      oneReportHighFactorKeys: on
        ? ONE_REPORT_KEYS.filter((candidate) => candidate === key || settings.oneReportHighFactorKeys.includes(candidate))
        : settings.oneReportHighFactorKeys.filter((candidate) => candidate !== key),
    });

  return (
    <section aria-labelledby="hr-rm-rules" className={layout.card} data-gap="md">
      <div className={layout.cardHeading}>
        <span className={layout.step}>{t('hrRiskMatrix.step', { step: 4, total: 4 })}</span>
        <Heading level={2} data-size="sm" id="hr-rm-rules">
          {t('hrRiskMatrix.rules.title')}
        </Heading>
        <Paragraph data-size="sm" className={layout.cardIntro}>
          {t('hrRiskMatrix.rules.intro')}
        </Paragraph>
      </div>
      <Switch
        data-size="sm"
        position="end"
        className={classes.fixedSwitch}
        label={t(text('hrRiskMatrix.rules.jointAssessment'))}
        description={t(text('hrRiskMatrix.rules.jointAssessmentDescription'))}
        checked
        readOnly
        onClick={preventToggle}
      />
      <Field data-size="sm" className={classes.mediumField}>
        <Label htmlFor="hr-rm-combined">{t('hrRiskMatrix.rules.combined')}</Label>
        <Field.Description id="hr-rm-combined-description">{t('hrRiskMatrix.rules.combinedDescription')}</Field.Description>
        <Select
          id="hr-rm-combined"
          aria-describedby="hr-rm-combined-description"
          value={settings.combinedAssessmentFrom}
          onChange={(event) => onChange({ combinedAssessmentFrom: Number(event.target.value) })}
        >
          {options.combinedAssessmentFrom.map((count) => (
            <Select.Option key={count} value={count}>
              {t('hrRiskMatrix.rules.combinedOption', { count })}
            </Select.Option>
          ))}
        </Select>
      </Field>
      <Switch
        data-size="sm"
        position="end"
        className={classes.fixedSwitch}
        label={t(text('hrRiskMatrix.rules.shared'))}
        description={t('hrRiskMatrix.rules.sharedDescription')}
        checked
        readOnly
        onClick={preventToggle}
      />
      <Fieldset data-size="sm" className={classes.oneReport}>
        <Fieldset.Legend>{t('hrRiskMatrix.rules.oneReport')}</Fieldset.Legend>
        <Paragraph data-size="sm" className={classes.oneReportText}>
          <Trans i18nKey="hrRiskMatrix.rules.oneReportDescription" components={{ strong: <strong /> }} />
        </Paragraph>
        {ONE_REPORT_KEYS.map((key) =>
          key === SEXUAL_HARASSMENT ? (
            <Checkbox
              key={key}
              label={factorName(key)}
              description={t('hrRiskMatrix.rules.alwaysOn')}
              checked
              readOnly
              onClick={preventToggle}
            />
          ) : (
            <Checkbox
              key={key}
              label={factorName(key)}
              checked={settings.oneReportHighFactorKeys.includes(key)}
              onChange={(event) => toggleOneReport(key, event.target.checked)}
            />
          ),
        )}
        <Field>
          <Label htmlFor={FIELD_IDS.whistleblowingUrl}>{t('hrRiskMatrix.rules.whistleblowing')}</Label>
          <Field.Description id={`${FIELD_IDS.whistleblowingUrl}-description`}>
            {t('hrRiskMatrix.rules.whistleblowingDescription')}
          </Field.Description>
          <Input
            id={FIELD_IDS.whistleblowingUrl}
            type="url"
            aria-describedby={`${FIELD_IDS.whistleblowingUrl}-description`}
            aria-invalid={(showUrlErrors && Boolean(whistleblowingError)) || undefined}
            value={settings.whistleblowingUrl ?? ''}
            onChange={(event) => onChange({ whistleblowingUrl: event.target.value })}
          />
          {showUrlErrors && whistleblowingError && (
            <ValidationMessage>{t('hrRiskMatrix.rules.whistleblowingError')}</ValidationMessage>
          )}
        </Field>
        <Field>
          <Label htmlFor={FIELD_IDS.followUpRoutineUrl}>{t('hrRiskMatrix.rules.followUp')}</Label>
          <Field.Description id={`${FIELD_IDS.followUpRoutineUrl}-description`}>
            {t('hrRiskMatrix.rules.followUpDescription')}
          </Field.Description>
          <Input
            id={FIELD_IDS.followUpRoutineUrl}
            type="url"
            aria-describedby={`${FIELD_IDS.followUpRoutineUrl}-description`}
            aria-invalid={(showUrlErrors && Boolean(followUpError)) || undefined}
            value={settings.followUpRoutineUrl ?? ''}
            onChange={(event) => onChange({ followUpRoutineUrl: event.target.value })}
          />
          {showUrlErrors && followUpError && (
            <ValidationMessage>{t('hrRiskMatrix.rules.followUpError')}</ValidationMessage>
          )}
        </Field>
        <Switch
          id={FIELD_IDS.sensitiveSkip}
          data-size="sm"
          position="end"
          className={classes.fixedSwitch}
          label={t('hrRiskMatrix.rules.sensitiveSkip')}
          description={t('hrRiskMatrix.rules.sensitiveSkipDescription')}
          checked={settings.allowSensitiveSkip}
          onChange={(event) => onChange({ allowSensitiveSkip: event.target.checked })}
        />
        <Paragraph data-size="xs" className={layout.subtle}>
          {t('hrRiskMatrix.rules.oneReportFootnote')}
        </Paragraph>
      </Fieldset>
      <Field data-size="sm" className={classes.minResponsesField}>
        <Label htmlFor={FIELD_IDS.minResponses}>{t('hrRiskMatrix.rules.minResponses')}</Label>
        <Field.Description id="hr-rm-min-responses-description">
          {t('hrRiskMatrix.rules.minResponsesDescription')}
        </Field.Description>
        <Select
          id={FIELD_IDS.minResponses}
          aria-describedby="hr-rm-min-responses-description"
          value={settings.minResponses}
          onChange={(event) => onChange({ minResponses: Number(event.target.value) })}
        >
          {options.minResponses.map((count) => (
            <Select.Option key={count} value={count}>
              {t('hrRiskMatrix.rules.responses', { count })}
            </Select.Option>
          ))}
        </Select>
      </Field>
    </section>
  );
};
